import React, { useCallback, useEffect, useState } from "react";

import { Box, Fade, Typography, debounce } from "@mui/material/";

import { CheckCircleRounded } from "@mui/icons-material";

/**
 * Offers the auto save functionality and a Autosave status component.
 *
 * Server side changes are not reflected in the UI.
 * Client side changes are debounced and sent to the server.
 */

const SAVED_TIMEOUT = 3000;
const SAVE_DEBOUNCE = 1000;

export type AutosaveStatusProps = {
  saved: boolean;
};
export type UseAutosaveResult<T> = AutosaveStatusProps & {
  // similar to onChange, but used for buttons / checkboxes
  onClick: (data: T) => void;

  inputProps: {
    value: T;
    onChange: (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => void;
    onBlur: () => void;
  };
};
export function useAutosave<T>({
  initialData,
  saveCallback,
}: {
  initialData: T;
  saveCallback: (data: T) => Promise<void>;
}) {
  const [saved, setSaved] = useState<boolean>(false);
  const [lastSavedValue, setLastSaved] = useState<T>(initialData);
  const [data, setData] = useState<T>(initialData);

  const save = useCallback(
    async (value: T) => {
      if (value === lastSavedValue) {
        return;
      }
      setSaved(false);
      try {
        await saveCallback(value);
        setLastSaved(value);
        setSaved(true);
        setTimeout(() => setSaved(false), SAVED_TIMEOUT);
      } catch (e) {
        console.error(e);
        setSaved(false);
      }
    },
    [lastSavedValue, saveCallback, setLastSaved, setSaved]
  );

  const onBlur = useCallback(() => save(data), [save, data]);
  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const data = event.target.value as unknown as T;
      setData(data);
      debounce(save, SAVE_DEBOUNCE)(data);
    },
    [save]
  );

  // for buttons / checkboxes
  const onClick = useCallback(
    (data: T) => {
      setData(data);
      save(data);
    },
    [save]
  );

  // set saved to false if data changes
  useEffect(() => {
    if (saved) {
      setSaved(data === lastSavedValue);
    }
  }, [data, lastSavedValue, saved]);

  return {
    saved,
    onClick,
    inputProps: {
      value: data,
      onChange,
      onBlur,
    },
  } as UseAutosaveResult<T>;
}

export const AutosaveStatus = ({ saved }: AutosaveStatusProps) => (
  <Fade in={saved}>
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.5,
        paddingLeft: 1,
      }}
    >
      <CheckCircleRounded
        sx={({ palette }) => ({ color: palette.primary.main })}
        fontSize={"small"}
      />
      <Typography variant={"caption"} textAlign={"center"}>
        Saved
      </Typography>
    </Box>
  </Fade>
);
